import { getEnvVariable } from '@/utils/get-env-variable';
import { forterDiagnostics } from '@/utils/forterDiagnostics';

const FORTER_SCRIPT_PATH = '/scripts/forter.js';
const FORTER_TOKEN_EVENT = 'ftr:tokenReady';
const FORTER_ID_TOKEN = getEnvVariable('VITE_FORTER_ID_TOKEN', '');

class ForterService {
  private forterToken: string = '';
  private initialized: boolean = false;
  private scriptElement: HTMLScriptElement | null = null;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    if (!FORTER_ID_TOKEN) {
      console.warn('Forter: VITE_FORTER_ID_TOKEN not configured');
      forterDiagnostics.notConfigured();
      return;
    }

    try {
      // Set up listener BEFORE loading script to catch the event
      this.setupTokenListener();
      await this.loadForterScript();
      this.initialized = true;
    } catch (error) {
      forterDiagnostics.initializeFailed(error);
    }
  }

  private loadForterScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof document === 'undefined') {
        reject(new Error('Document is not available'));
        return;
      }

      const existingScript = document.getElementById('forter-script');
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = 'forter-script';
      script.src = FORTER_SCRIPT_PATH;
      script.setAttribute('data-siteId', FORTER_ID_TOKEN);
      // Perf note (ACDEV-27224): this used to carry `fetchpriority="low"` on
      // the theory that "the token is only consumed at pay-time, so a later
      // first-byte is fine." That's false on fast checkouts (Apple Pay tab
      // fallback, express one-tap): the vendor script writes the
      // `forterToken` cookie synchronously on execution, so a late first
      // byte can push that write past pay-time, shipping an empty
      // `forterTokenCookie` to the BE.
      //
      // `async` scripts default to LOW network priority in Chromium
      // regardless of any other markup — `fetchpriority="low"` on an async
      // script is a no-op, but so is leaving it unset. `high` is the only
      // way to actually win the byte-priority queue against same/higher
      // priority resources (Stripe's script, `/checkout/v3`, JS chunks)
      // without making this render-blocking. Safe to prioritize: nothing
      // else on this page currently claims `high`, so this isn't diluting
      // an existing boost or starving Stripe (which defaults to the same
      // Low priority and has more slack — `loadStripe()` is gated behind
      // `/checkout/v3` resolving anyway).
      script.setAttribute('fetchpriority', 'high');
      script.async = true;

      script.onload = () => {
        forterDiagnostics.scriptLoaded();
        resolve();
      };
      script.onerror = () => {
        forterDiagnostics.scriptLoadFailed();
        reject(new Error('Failed to load Forter script'));
      };

      document.head.appendChild(script);
      this.scriptElement = script;
      forterDiagnostics.scriptLoadStarted();
    });
  }

  private setupTokenListener(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const eventListener = (event: Event) => {
      const customEvent = event as CustomEvent;
      const token = customEvent.detail;

      if (token) {
        this.forterToken = token;
        console.log('Forter: Token received and stored');
        forterDiagnostics.tokenReceived();
      } else {
        console.warn('Forter: Token event fired but token is empty');
      }
    };

    document.addEventListener(FORTER_TOKEN_EVENT, eventListener);
    console.log('Forter: Event listener set up for', FORTER_TOKEN_EVENT);
  }

  getToken(): string {
    return this.forterToken;
  }

  isReady(): boolean {
    return this.initialized && this.forterToken !== '';
  }

  /** Whether `initialize()` completed — the one piece of funnel state this service, not `forterDiagnostics`, actually owns. */
  isInitialized(): boolean {
    return this.initialized;
  }

  cleanup(): void {
    if (this.scriptElement && this.scriptElement.parentNode) {
      this.scriptElement.parentNode.removeChild(this.scriptElement);
      this.scriptElement = null;
    }
    this.forterToken = '';
    this.initialized = false;
    forterDiagnostics.reset();
  }
}

export const forterService = new ForterService();
